import React from "react";
import { useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../assets/theme";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { countries } from "../assets/utils/countries";
import CustomLabel from "../components/CustomLabel";
import { getAllBranches } from "../services/BranchesService";
import {
  getAllStaff,
  createStaff,
  updateStaff,
  deleteStaff,
} from "../services/StaffService";
import { getAllSpecialities } from "../services/SpecialitiesService";
import { getUserId } from "../services/authService";
import { useParams, useLocation } from "react-router-dom";
import { hashPassword } from "../utils/passwordUtils";

const Staff: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const venueId = location.state?.venueId || params.branchId || null;
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );
  const [branches, setBranches] = React.useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = React.useState(false);
  const [specialities, setSpecialities] = React.useState<any[]>([]);
  const [loadingSpecialities, setLoadingSpecialities] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<any[]>([]);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);

  const [search, setSearch] = React.useState<string>("");

  // Fetch branches first
  React.useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        if (!venueId) {
          console.error("No venue ID available");
          setBranches([]);
          return;
        }

        const response = await getAllBranches(venueId);
        console.log("Branches fetched:", response);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        setBranches(Array.isArray(branchesData) ? branchesData : []);
        console.log("Branches array set:", branchesData);
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, [venueId]);

  // Fetch specialities
  React.useEffect(() => {
    const fetchSpecialities = async () => {
      setLoadingSpecialities(true);
      try {
        const response = await getAllSpecialities();
        console.log("Specialities fetched:", response);

        let specialitiesData = [];
        if (response && response.specialities) {
          specialitiesData = response.specialities;
        } else if (response && response.data) {
          specialitiesData = response.data;
        } else if (Array.isArray(response)) {
          specialitiesData = response;
        }

        setSpecialities(
          Array.isArray(specialitiesData) ? specialitiesData : []
        );
        console.log("Specialities array set:", specialitiesData);
      } catch (error) {
        console.error("Error fetching specialities:", error);
        setSpecialities([]);
      } finally {
        setLoadingSpecialities(false);
      }
    };

    fetchSpecialities();
  }, []);

  // Fetch staff from API
  React.useEffect(() => {
    const fetchStaff = async () => {
      if (!venueId) {
        console.error("No venue ID available for fetching staff");
        return;
      }

      if (loadingBranches) {
        return;
      }

      const branchId =
        branches.length > 0
          ? branches[0].business_id || branches[0].id || branches[0]._id
          : null;

      if (!branchId) {
        console.error("No branch ID available for fetching staff");
        setRows([]);
        return;
      }

      setLoading(true);
      try {
        const response = await getAllStaff();
        console.log("Staff fetched:", response);

        let staffData = [];

        // Handle different response structures
        if (response && response.user) {
          // Single user object
          staffData = [response.user];
        } else if (response && response.users) {
          // Multiple users array
          staffData = response.users;
        } else if (response && response.staff) {
          staffData = response.staff;
        } else if (response && response.data) {
          if (response.data.user) {
            staffData = [response.data.user];
          } else if (response.data.users) {
            staffData = response.data.users;
          } else if (Array.isArray(response.data)) {
            staffData = response.data;
          }
        } else if (Array.isArray(response)) {
          staffData = response;
        }

        const mappedStaff = staffData.map((staff: any) => ({
          id: staff.id,
          name: staff.name,
          email: staff.email,
          country_code: staff.country_code,
          mobile: staff.mobile,
          speciality: staff.speciality,
          active: staff.active === "verified" ? "Verified" : "Unverified",
          business_ids: staff.business_ids || staff.admin_user_id || branchId,
          client_number: staff.client_number,
          color: staff.color,
        }));

        console.log("Mapped staff:", mappedStaff);
        setRows(mappedStaff);
      } catch (error: any) {
        console.error("Error fetching staff:", error);
        const errorMessage =
          error?.response?.data?.error_description ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to fetch staff.";
        toast.error(errorMessage, {
          position: "top-right",
        });
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, [venueId, refetchTrigger, branches, loadingBranches]);

  const formFields: Field[] = [
    { name: "name", label: "Name" },
    { name: "email", label: "Email" },
    {
      name: "country_code",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => `+${c.phone}`),
    },
    { name: "mobile", label: "Mobile" },
    {
      name: "speciality_id",
      label: "Role/Speciality ID",
    },
    {
      name: "active",
      label: "Status",
      type: "dropdown",
      options: ["Verified", "Unverified"],
    },
    {
      name: "business_id",
      label: "Branch/Business",
      type: "dropdown",
      options: branches.map((branch) => ({
        label: branch.name || "Unknown Branch",
        value: branch.business_id || branch.id || branch._id,
      })),
    },
  ];

  const formFieldsForCreate: Field[] = [
    { name: "name", label: "Name" },
    { name: "email", label: "Email" },
    {
      name: "password",
      label: "Password",
      type: "password",
    },
    {
      name: "country_code",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => `+${c.phone}`),
    },
    { name: "mobile", label: "Mobile" },
    {
      name: "speciality_id",
      label: "Role/Speciality ID",
    },
    {
      name: "active",
      label: "Status",
      type: "dropdown",
      options: ["Verified", "Unverified"],
    },
    {
      name: "business_id",
      label: "Branch/Business",
      type: "dropdown",
      options: branches.map((branch) => ({
        label: branch.name || "Unknown Branch",
        value: branch.business_id || branch.id || branch._id,
      })),
    },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
    onPdf: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const exportCsv = React.useCallback(() => {
    if (!rows || rows.length === 0) return;
    const keys = [
      "id",
      "name",
      "email",
      "country_code",
      "mobile",
      "speciality",
      "active",
    ];
    const header = keys.join(",");
    const lines = rows.map((r: any) =>
      keys
        .map((k) => {
          const v = r[k] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="Staff"
          variant="h4"
          fontWeight="seimi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              label: "Export CSV",
              onClick: () => Promise.resolve(exportCsv()),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            { key: "name", header: "Name" },
            { key: "email", header: "Email" },
            { key: "country_code", header: "Country Code" },
            { key: "mobile", header: "Mobile" },
            { key: "speciality", header: "Role" },
            { key: "active", header: "Status" },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const safeRows = Array.isArray(rows) ? rows : [];

            const filtered = safeRows.filter((r: any) => {
              if (!normalizedQuery) return true;

              const haystack = [
                r.name,
                r.email,
                r.country_code,
                r.mobile,
                r.speciality,
                r.active,
              ]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Staff` : "Add Staff"}
          fields={editingRow ? formFields : formFieldsForCreate}
          initialValues={
            editingRow
              ? Object.keys(editingRow).reduce((acc: any, key) => {
                  const val = editingRow[key];
                  acc[key] =
                    val === undefined || val === null ? "" : String(val);
                  return acc;
                }, {})
              : { active: "Verified" }
          }
          onSubmit={async (values: any) => {
            try {
              const userId = getUserId();

              if (editingRow) {
                // Update existing staff
                const updateData = {
                  name: values.name,
                  email: values.email,
                  mobile: values.mobile,
                  country_code: values.country_code,
                  speciality_id: values.speciality_id,
                  active:
                    values.active === "Verified" ? "verified" : "unverified",
                };

                await updateStaff(editingRow.id, updateData);
                toast.success("Staff updated successfully!", {
                  position: "top-right",
                });
              } else {
                // Create new staff - hash the password first
                const hashedPassword = await hashPassword(values.password);

                const staffData = {
                  name: values.name,
                  email: values.email,
                  password: hashedPassword,
                  country_code: values.country_code,
                  mobile: values.mobile,
                  speciality_id: values.speciality_id,
                  active:
                    values.active === "Verified" ? "verified" : "unverified",
                  business_ids: [values.business_id], // API expects an array
                };

                await createStaff(staffData);
                toast.success("Staff created successfully!", {
                  position: "top-right",
                });
              }

              setRefetchTrigger((prev) => prev + 1);
              setEditOpen(false);
            } catch (error: any) {
              console.error("Failed to save staff", error);
              const errorMessage =
                error?.response?.data?.error_description ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to save staff. Please try again.";
              toast.error(errorMessage, {
                position: "top-right",
              });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteStaff(String(toDeleteId));
              toast.success("Staff deleted successfully!", {
                position: "top-right",
              });
              setRefetchTrigger((prev) => prev + 1);
              setConfirmOpen(false);
              setToDeleteId(null);
            } catch (error: any) {
              console.error("Failed to delete staff", error);
              const errorMessage =
                error?.response?.data?.error_description ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to delete staff. Please try again.";
              toast.error(errorMessage, {
                position: "top-right",
              });
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Staff;
